import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import React, { useRef } from 'react';
import { BlockEmbed } from 'quill/blots/block';
import Quill from 'quill';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import styles from './PostLayout.module.scss';
import { usePostLayoutSize, usePostLayoutState } from './PostLayoutProvider';
import { useQuill } from '../editor/QuillContext';

interface ContainerProps {
  children: React.ReactNode;
  hasBanner: boolean;
  hasStateBar: boolean;
  hasMagneticWallItems: boolean;
}

export const Container = React.forwardRef<HTMLElement, ContainerProps>(
  ({ children, hasBanner, hasStateBar, hasMagneticWallItems }, ref) => {
    const postLayoutState = usePostLayoutState();
    const postLayoutSize = usePostLayoutSize();
    const isSidebarOpen = useIsSidebarOpen();

    return (
      <article
        ref={ref}
        className={classNames(
          styles.postLayoutContainer,
          styles[postLayoutSize],
          {
            [styles.hasBanner]: hasBanner,
            [styles.hasStateBar]: hasStateBar,
            [styles.isTOCExpanded]: postLayoutState.isTOCExpanded,
            [styles.isSidebarOpen]: isSidebarOpen,
            [styles.hasMagneticWallItems]: hasMagneticWallItems,
          },
        )}
      >
        {children}
      </article>
    );
  },
);

interface AboveProps {
  children?: React.ReactNode;
}

export const Above = React.forwardRef<HTMLElement, AboveProps>(
  ({ children }, ref) => (
    <header ref={ref} className={styles.postLayoutAbove}>
      {children}
    </header>
  ),
);

interface ControlBarProps {
  children: React.ReactNode;
}

export const ControlBar = ({ children }: ControlBarProps) => (
  <div className={styles.postLayoutControlBar}>{children}</div>
);

interface FooterControlBarProps {
  children: React.ReactNode;
}

export const FooterControlBar = ({ children }: FooterControlBarProps) => (
  <div
    // TODO: remove when releasing footer insights button
    style={{ display: 'none' }}
    className={styles.postLayoutFooterControlBar}
  >
    {children}
  </div>
);

interface EditorProps {
  children: React.ReactNode;
  onDoubleClick?: (event: React.MouseEvent) => void;
}

export const Editor = ({ children, onDoubleClick }: EditorProps) => (
  <article
    className={styles.postLayoutEditor}
    aria-label="Post content"
    onDoubleClick={onDoubleClick}
  >
    {children}
  </article>
);

export const MagneticWall = React.forwardRef<
  HTMLElement,
  HTMLAttributes<HTMLElement>
>(({ children, ...rest }, ref) => (
  <aside ref={ref} className={styles.postLayoutMagneticWall} {...rest}>
    {children}
  </aside>
));

interface BelowProps {
  children: React.ReactNode;
}

export const Below = ({ children }: BelowProps) => {
  const footerRef = useRef<HTMLElement>(null);
  const { quill, readOnly } = useQuill();
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <footer
      className={classNames(styles.postLayoutBelow, {
        [styles.readOnly]: readOnly,
      })}
      onClick={() => {
        if (!footerRef.current || !quill || readOnly) return;

        if (!footerRef.current.hasChildNodes()) {
          const [lastLine] = quill.getLine(quill.getLength() - 1);
          const isLastLineEmbed = lastLine instanceof BlockEmbed;

          if (isLastLineEmbed) {
            quill.insertText(quill.getLength(), '\n', Quill.sources.USER);
          }

          quill.setSelection(
            { index: quill.getLength() - 1, length: 0 },
            Quill.sources.USER,
          );
        }
      }}
      ref={footerRef}
    >
      {children}
    </footer>
  );
};

interface BannerProps {
  children: React.ReactNode;
  className?: string;
}

export const Banner = React.forwardRef<HTMLDivElement, BannerProps>(
  ({ children, className }, ref) => (
    <div ref={ref} className={classNames(styles.postLayoutBanner, className)}>
      {children}
    </div>
  ),
);

interface ControlsProps {
  children: React.ReactNode;
  className: string;
}

export const Controls = ({ children, className }: ControlsProps) => (
  <div className={classNames(styles.postLayoutBannerControls, className)}>
    {children}
  </div>
);

export const TOC = React.forwardRef<HTMLElement, HTMLAttributes<HTMLElement>>(
  ({ children, className, ...rest }, ref) => (
    <aside
      ref={ref}
      className={classNames(className, styles.postLayoutTOC)}
      {...rest}
    >
      {children}
    </aside>
  ),
);

export const Gutter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...rest }, ref) => (
  <div
    ref={ref}
    className={classNames(className, styles.postLayoutGutter)}
    {...rest}
  >
    {children}
  </div>
));
